import { computed, Injectable, signal, Signal, WritableSignal } from '@angular/core';

const STORAGE_KEY: Readonly<string> = 'color-mode';
const COLOR_MODE_ATTRIBUTE: Readonly<string> = 'data-color-mode';

export type ColorMode = 'light' | 'dark';

// Drives the Stride `[data-color-mode='dark']` switch on the document root.
// Light is the :root default, so it clears the attribute rather than setting it.
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private mode: WritableSignal<ColorMode> = signal<ColorMode>(this.initialMode());

  public colorMode: Signal<ColorMode> = this.mode.asReadonly();
  public isDark: Signal<boolean> = computed((): boolean => this.mode() === 'dark');

  constructor() {
    this.apply(this.mode());
  }

  public toggle(): void {
    this.set(this.mode() === 'dark' ? 'light' : 'dark');
  }

  public set(mode: ColorMode): void {
    this.mode.set(mode);
    localStorage.setItem(STORAGE_KEY, mode);
    this.apply(mode);
  }

  private initialMode(): ColorMode {
    const stored: string | null = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private apply(mode: ColorMode): void {
    if (mode === 'dark') document.documentElement.setAttribute(COLOR_MODE_ATTRIBUTE, 'dark');
    else document.documentElement.removeAttribute(COLOR_MODE_ATTRIBUTE);
  }
}
